import { TableClass } from '@thinkalpha/ui-types/contracts/workspace';
import { escapeFormulaStringLiteral } from 'src/lib/escapeFormulaStringLiteral';
import type { TableLinkRow } from 'src/services/WorkspaceService/linking';

const OPEN_ORDERS_FILTER = '(RemainingShares > 0)';

function comparisons(rows: readonly TableLinkRow[], field: string, rowField: keyof TableLinkRow): string | undefined {
    const clauses: string[] = [];
    for (const row of rows) {
        const value = row[rowField];
        if (value !== undefined) {
            clauses.push(`(str_cmp(${field}, '$${escapeFormulaStringLiteral(value)}') == 0)`);
        }
    }
    return clauses.join(' or ') || undefined;
}

function openOrdersFilter(filter: string | undefined): string {
    return filter === undefined ? OPEN_ORDERS_FILTER : `${filter} and ${OPEN_ORDERS_FILTER}`;
}

export function canLinkTableClasses(source: TableClass, target: TableClass): boolean {
    switch (source) {
        case TableClass.platforms:
            return [TableClass.accounts, TableClass.orders, TableClass.fills, TableClass.positions].includes(target);
        case TableClass.accounts:
            return [TableClass.positions, TableClass.orders, TableClass.fills].includes(target);
        case TableClass.symbols:
            return [TableClass.positions, TableClass.fills, TableClass.orders].includes(target);
        case TableClass.traders:
            return target === TableClass.positions;
        case TableClass.positions:
            return [TableClass.orders, TableClass.fills].includes(target);
        default:
            return false;
    }
}

/** Reproduces the private Table-to-Table formulas used by the legacy ResultsRowClick channel. */
export function makeTableLinkFilter(
    source: TableClass,
    target: TableClass,
    rows: readonly TableLinkRow[],
): string | undefined {
    switch (source) {
        case TableClass.platforms:
            if (target === TableClass.accounts) {
                const clauses: string[] = [];
                for (const row of rows) {
                    if (row.positionKey !== undefined) {
                        clauses.push(`(PositionKey like '%.*:${row.positionKey}')`);
                    }
                }
                return clauses.join(' or ') || undefined;
            }
            if (target === TableClass.orders) return openOrdersFilter(comparisons(rows, 'Platform', 'platform'));
            if (target === TableClass.fills || target === TableClass.positions) {
                return comparisons(rows, 'Platform', 'platform');
            }
            return undefined;
        case TableClass.accounts:
            if (target === TableClass.positions) {
                const clauses: string[] = [];
                for (const row of rows) {
                    if (row.positionKey === undefined) continue;
                    const [account, platform] = row.positionKey.split(':');
                    if (platform === undefined) continue;
                    clauses.push(`(PositionKey like '%${account}-.*:${platform}')`);
                }
                return clauses.join(' or ') || undefined;
            }
            if (target === TableClass.orders) return openOrdersFilter(comparisons(rows, 'Account', 'account'));
            if (target === TableClass.fills) return comparisons(rows, 'Account', 'account');
            return undefined;
        case TableClass.symbols:
            if (target === TableClass.orders) return openOrdersFilter(comparisons(rows, 'Symbol', 'symbol'));
            if (target === TableClass.positions || target === TableClass.fills) {
                return comparisons(rows, 'Symbol', 'symbol');
            }
            return undefined;
        case TableClass.traders:
            return target === TableClass.positions ? comparisons(rows, 'OwnerID', 'traderId') : undefined;
        case TableClass.positions:
            if (target === TableClass.orders) return openOrdersFilter(comparisons(rows, 'Symbol', 'symbol'));
            return target === TableClass.fills ? comparisons(rows, 'Symbol', 'symbol') : undefined;
        default:
            return undefined;
    }
}
